import { useCallback, useMemo } from "react";
import { ScrollView } from "react-native";
import { useRouter, useLocalSearchParams, usePathname } from "expo-router";
import { View, Text } from "@src/components";
import { useCamerasListState, type CameraApiResponse } from "@src/features/cameras";
import { getCameraKeyString, getCameraSourceAndFleet } from "@src/utils/key-parser";
import { CameraListItem } from "@src/features/cameras";
import { SidepanelSiteGroup } from "./SidepanelSiteGroup.web";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { useCameraCollection } from "@src/features/cameras/hooks/useCameraCollection";
import { CameraListItemSidepanel } from "@src/features/cameras/components/CameraListItemSidepanel.web";
import { useSnapshots } from "@visioni/a3s-media/app";

interface SidepanelCameraListProps {
  collapsed?: boolean;
  selectedSiteId?: string | null;
}

export function SidepanelCameraList({ collapsed = false, selectedSiteId }: SidepanelCameraListProps) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useLocalSearchParams<{ cameraKey?: string; siteId?: string }>();
  const { track } = useAmplitude();

  const siteId = params.siteId;

  const listState = useCamerasListState(siteId);
  const { data: snapshots } = useSnapshots({});

  const allCameras = listState.status === "success" ? listState.cameras : undefined;
  const filteredBySite = useMemo(() => {
    if (!allCameras) return undefined;
    if (!selectedSiteId) return allCameras;
    return allCameras.filter((camera) => camera.site.id === selectedSiteId);
  }, [allCameras, selectedSiteId]);

  const { groupedCameras } = useCameraCollection({
    cameras: filteredBySite,
    searchQuery: "",
    sortBy: [],
  });

  // Determine navigation context - are we on camera details or alarm settings?
  const isAlarmSettingsScreen = pathname.includes("/alarm");

  const handleCameraSelect = useCallback(
    (camera: CameraApiResponse) => {
      const cameraKey = getCameraKeyString(camera);
      const cameraTenantId = camera.site.tenant;
      const cameraSiteId = camera.site.id;

      // Track camera click analytics
      const sourceAndFleet = getCameraSourceAndFleet(camera);
      track(ANALYTICS_EVENTS.CAMERA_CLICKED, {
        camera_id: cameraKey,
        ...(sourceAndFleet && {
          source: sourceAndFleet.source,
          generation: sourceAndFleet.fleet,
        }),
        tenant_id: cameraTenantId,
        site_id: cameraSiteId,
      });

      if (isAlarmSettingsScreen) {
        // Navigate to alarm settings for the selected camera
        router.push(`/tenants/${cameraTenantId}/sites/${cameraSiteId}/cameras/${cameraKey}/alarm` as never);
      } else {
        // Navigate to camera details
        router.push(`/tenants/${cameraTenantId}/sites/${cameraSiteId}/cameras/${cameraKey}` as never);
      }
    },
    [router, isAlarmSettingsScreen, track],
  );

  if (listState.status === "pending") {
    return (
      <View flex={1} justifyContent="center" alignItems="center" py="6xl">
        <Text textStyle="sm/regular" color="textMuted">
          Loading cameras...
        </Text>
      </View>
    );
  }

  if (listState.status === "error") {
    return (
      <View flex={1} justifyContent="center" alignItems="center" py="6xl">
        <Text textStyle="sm/regular" color="textMuted">
          Failed to load cameras
        </Text>
      </View>
    );
  }

  // Empty state
  if (groupedCameras.length === 0) {
    return (
      <View flex={1} justifyContent="center" alignItems="center" py="6xl">
        <Text textStyle="sm/regular" color="textMuted">
          {"No cameras available"}
        </Text>
      </View>
    );
  }

  const hasSingleSite = groupedCameras.length === 1;

  // Single site: flat list without site grouping
  if (hasSingleSite) {
    const cameras = groupedCameras[0].cameras;
    return (
      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
        <View gap="lg">
          {cameras.map((camera) => {
            const cameraKey = getCameraKeyString(camera);
            const isSelected = cameraKey === params.cameraKey;
            return (
              <CameraListItemSidepanel
                key={cameraKey}
                camera={camera}
                siteId={camera.site.id}
                onSelect={() => handleCameraSelect(camera)}
                href={
                  isAlarmSettingsScreen ?
                    (`/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(cameraKey)}/alarm` as never)
                  : (`/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(cameraKey)}` as never)
                }
                isSelected={isSelected}
                collapsed={collapsed}
                snapshot={snapshots?.[`${camera.key.asset}@${camera.key.id}`]}
              />
            );
          })}
        </View>
      </ScrollView>
    );
  }

  // Multiple sites: grouped view with site headers
  return (
    <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
      <View gap="lg">
        {groupedCameras.map((group, index) => {
          const containsSelectedCamera = group.cameras.some(
            (camera) => getCameraKeyString(camera) === params.cameraKey,
          );

          return (
            <SidepanelSiteGroup
              key={group.siteId}
              siteName={group.siteName}
              siteSubtitle={group.siteSubtitle}
              thumbnailUrl={group.thumbnailUrl}
              cameras={group.cameras}
              selectedCameraKey={params.cameraKey}
              onCameraSelect={handleCameraSelect}
              isAlarmSettingsScreen={isAlarmSettingsScreen}
              defaultExpanded={containsSelectedCamera || index === 0}
              collapsed={collapsed}
              snapshots={snapshots}
            />
          );
        })}
      </View>
    </ScrollView>
  );
}
